import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, UserPlus, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const OTHER_VALUE = "__other__";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [age, setAge] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [newSchoolName, setNewSchoolName] = useState("");
  const [grade, setGrade] = useState("");
  const [schools, setSchools] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState(null);

  // Schools + grades are public (no auth required) so we can load them on
  // the registration screen. Schools come back sorted alphabetically from
  // the server; we re-sort defensively in case any caller serves stale data.
  useEffect(() => {
    (async () => {
      try {
        const [s, g] = await Promise.all([api.get("/schools"), api.get("/grades")]);
        const sorted = [...(s.data || [])].sort((a, b) =>
          (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())
        );
        setSchools(sorted);
        setGrades(g.data || []);
      } catch (err) {
        console.warn("[Register] could not preload schools/grades:", err);
      }
    })();
  }, []);

  const isOther = schoolId === OTHER_VALUE;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 4) { toast.error("Password must be at least 4 characters"); return; }
    if (isOther && !newSchoolName.trim()) {
      toast.error("Please enter your school name");
      return;
    }
    setLoading(true);
    try {
      const payload = { name, email, password };
      if (age) payload.age = parseInt(age, 10);
      if (isOther) {
        payload.new_school_name = newSchoolName.trim();
      } else if (schoolId) {
        payload.school_id = schoolId;
      }
      if (grade) payload.grade_level = grade;
      const { data } = await api.post("/auth/register", payload);
      setRegisteredEmail(data.email || email);
      if (isOther) {
        toast.info("Your school is pending approval. You can still use the app while we review it.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally { setLoading(false); }
  };

  if (registeredEmail) {
    return (
      <div className="min-h-screen bg-[#F4F9FF] bg-polka flex items-center justify-center p-5">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", bounce: 0.5, duration: 0.6 }}
          className="w-full max-w-md"
        >
          <div className="card-soft text-center space-y-4" data-testid="register-check-email">
            <div className="inline-flex w-20 h-20 rounded-3xl bg-[#4D96FF] items-center justify-center shadow-[0_8px_0_#1D4ED8] mb-2">
              <MailCheck className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
            <h1 className="font-display text-3xl font-bold text-[#1E1B4B]">Check your email!</h1>
            <p className="text-slate-600 font-semibold">
              We sent a confirmation link to<br />
              <span className="text-[#1E1B4B]">{registeredEmail}</span>.
            </p>
            <p className="text-sm text-slate-500">
              Click the link in that email to activate your account, then come back and log in.
            </p>
            <Link to="/login" className="btn-clay w-full inline-flex" data-testid="register-check-email-login-link">
              Go to login
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F9FF] bg-polka flex items-center justify-center p-5">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", bounce: 0.5, duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6">
          <div className="inline-flex w-20 h-20 rounded-3xl bg-[#FF6B6B] items-center justify-center shadow-[0_8px_0_#9F1239] mb-4">
            <Sparkles className="w-10 h-10 text-white" strokeWidth={3} />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#1E1B4B]">Create account</h1>
          <p className="text-lg text-slate-600 mt-1 font-semibold">Start your spelling journey</p>
        </div>

        <form onSubmit={onSubmit} className="card-soft space-y-4" data-testid="register-form">
          <div>
            <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">Your name</label>
            <input
              required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-5 py-4 text-lg outline-none transition-all font-semibold"
              placeholder="Alex"
              data-testid="register-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">Email</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-5 py-4 text-lg outline-none transition-all font-semibold"
              placeholder="alex@school.com"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">Password</label>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-5 py-4 text-lg outline-none transition-all font-semibold"
              placeholder="At least 4 characters"
              data-testid="register-password-input"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">School</label>
            <select
              value={schoolId}
              onChange={(e) => setSchoolId(e.target.value)}
              className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-4 text-base outline-none transition-all font-semibold bg-white"
              data-testid="register-school-input"
            >
              <option value="">— pick your school —</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
              <option value={OTHER_VALUE}>Other (add a new school)</option>
            </select>
            {isOther && (
              <input
                value={newSchoolName}
                onChange={(e) => setNewSchoolName(e.target.value)}
                placeholder="Type your school's name"
                className="mt-2 w-full rounded-2xl border-4 border-amber-200 focus:border-[#FBBF24] px-5 py-3 text-base outline-none transition-all font-semibold"
                data-testid="register-new-school-input"
                maxLength={80}
              />
            )}
            {isOther && (
              <p className="text-xs text-amber-700 mt-1 ml-2 font-semibold" data-testid="register-pending-school-note">
                Your school will be added as <span className="underline">pending</span> until an admin approves it.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">Age</label>
              <input
                type="number" min="3" max="99" value={age} onChange={(e) => setAge(e.target.value)}
                className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-5 py-4 text-lg outline-none transition-all font-semibold"
                placeholder="7"
                data-testid="register-age-input"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">Grade</label>
              <select
                value={grade} onChange={(e) => setGrade(e.target.value)}
                className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-4 text-base outline-none transition-all font-semibold bg-white"
                data-testid="register-grade-input"
              >
                <option value="">— pick later —</option>
                {grades.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-clay w-full" data-testid="register-submit-btn">
            <UserPlus className="w-5 h-5" strokeWidth={3} /> {loading ? "Creating…" : "Create account"}
          </button>
          <p className="text-center text-sm text-slate-600 pt-2">
            Have an account?{" "}
            <Link to="/login" className="font-bold text-[#4D96FF] hover:underline" data-testid="go-login-link">
              Log in
            </Link>
          </p>
        </form>
      </motion.div>
    </div>
  );
}
